import { useQuery } from '@tanstack/react-query'
import { analyticsFetch, getCallerAgentId, hasLiveAnalytics } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import {
  toPnlSummary,
  type AnalyticsDailyResponse,
  type AnalyticsPnlSummary,
} from '@/api/map/pnl'
import * as api from '@/data/api'
import { keys } from '@/api/hooks/shared/keys'

function rangeQuery(range: { from: string; to: string }): string {
  return new URLSearchParams({
    from: range.from.slice(0, 10),
    to: range.to.slice(0, 10),
  }).toString()
}

function emptyPnl(query: api.PnlQuery): api.PnlSummary {
  return {
    rows: [],
    total: 0,
    page: query.page ?? 1,
    pageSize: query.pageSize ?? 50,
    totals: {
      turnover: 0,
      memberPnl: 0,
      commission: 0,
      netPnl: 0,
      bets: 0,
      members: 0,
      margin: 0,
    },
    daily: [],
    splitByRole: [],
  }
}

async function loadPnl(query: api.PnlQuery): Promise<api.PnlSummary> {
  if (hasLiveAnalytics()) {
    const id = query.rootId ?? getCallerAgentId()
    if (!id) return emptyPnl(query)
    try {
      const qs = rangeQuery(query.range)
      const [pnl, daily] = await Promise.all([
        analyticsFetch<AnalyticsPnlSummary>(`/v1/agents/${id}/pnl?${qs}`),
        analyticsFetch<AnalyticsDailyResponse>(`/v1/agents/${id}/daily?${qs}`).catch(
          () => ({ rows: [] as AnalyticsDailyResponse['rows'] }),
        ),
      ])
      return toPnlSummary(pnl, daily.rows, {
        page: query.page,
        pageSize: query.pageSize,
        myRole: query.myRole,
      })
    } catch (err) {
      if (isUnreachableApiError(err)) return emptyPnl(query)
      throw err
    }
  }
  return api.getPnl(query)
}

export function usePnl(query: api.PnlQuery) {
  return useQuery({
    queryKey: keys.pnl(query),
    queryFn: () => loadPnl(query),
    placeholderData: (previous) => previous,
  })
}
